import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { resumeData } from '../data/resumeData';

const ease = [0.22, 1, 0.36, 1];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease }
});

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } }
};

const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease } }
};

function SideBlock({ label, children, delay }) {
  return (
    <motion.div {...fadeUp(delay)} className="border-l border-white/15 pl-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">{label}</p>
      <div className="mt-3 text-sm text-white/85">{children}</div>
    </motion.div>
  );
}

function Resume({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="resume-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[90] flex items-stretch justify-center bg-black/70 backdrop-blur-md md:items-center md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={`${resumeData.name} resume`}
        >
          <motion.div
            key="resume-panel"
            initial={{ opacity: 0, y: 60, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: 0.62, ease }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex max-h-full w-full max-w-6xl flex-col overflow-hidden bg-coal text-white shadow-2xl md:rounded-3xl"
          >
            <div className="pointer-events-none absolute inset-0 opacity-30">
              <div className="absolute -left-32 top-20 h-72 w-72 rounded-full bg-accent/40 blur-3xl" />
              <div className="absolute -right-32 bottom-10 h-80 w-80 rounded-full bg-accent/30 blur-3xl" />
            </div>

            <header className="relative flex items-center justify-between border-b border-white/10 px-5 py-4 md:px-8 md:py-5">
              <div className="flex items-center gap-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/70" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
                </span>
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/65">Live Resume</p>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={`mailto:${resumeData.contact.email}`}
                  className="hidden rounded-full border border-white/20 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85 transition-colors hover:bg-white/10 sm:inline-flex"
                >
                  Hire me
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close resume"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/5 transition-colors hover:bg-white/15"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
            </header>

            <div className="relative grid grid-cols-1 gap-0 overflow-y-auto md:grid-cols-12">
              <aside className="relative flex flex-col gap-7 border-b border-white/10 bg-black/30 px-6 py-8 md:col-span-4 md:border-b-0 md:border-r md:px-8 md:py-10">
                <motion.div initial="hidden" animate="show" variants={stagger}>
                  <motion.div variants={item}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">Contact</p>
                  </motion.div>
                  <motion.ul variants={stagger} className="mt-4 space-y-3 text-sm text-white/85">
                    <motion.li variants={item} className="flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" /> {resumeData.contact.city}
                    </motion.li>
                    <motion.li variants={item}>
                      <a href={`tel:${resumeData.contact.phone}`} className="hover:text-accent">
                        {resumeData.contact.phone}
                      </a>
                    </motion.li>
                    <motion.li variants={item}>
                      <a href={`mailto:${resumeData.contact.email}`} className="break-all hover:text-accent">
                        {resumeData.contact.email}
                      </a>
                    </motion.li>
                    <motion.li variants={item}>
                      <a
                        href={`https://${resumeData.contact.linkedin}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-accent"
                      >
                        {resumeData.contact.linkedin}
                      </a>
                    </motion.li>
                    <motion.li variants={item}>
                      <a
                        href={`https://${resumeData.contact.portfolio}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-accent"
                      >
                        {resumeData.contact.portfolio}
                      </a>
                    </motion.li>
                  </motion.ul>
                </motion.div>

                <SideBlock label="Top Skills" delay={0.25}>
                  <div className="flex flex-wrap gap-2">
                    {resumeData.topSkills.map((s, i) => (
                      <motion.span
                        key={s}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: 0.3 + i * 0.06, ease }}
                        className="rounded-full border border-accent/45 bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white"
                      >
                        {s}
                      </motion.span>
                    ))}
                  </div>
                </SideBlock>

                <SideBlock label="Certifications" delay={0.45}>
                  <ul className="space-y-2">
                    {resumeData.certifications.map((c, i) => (
                      <motion.li
                        key={c}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.42, delay: 0.5 + i * 0.05, ease }}
                        className="flex items-start gap-2 text-sm text-white/80"
                      >
                        <span className="mt-[7px] inline-block h-1 w-2.5 bg-accent/70" />
                        {c}
                      </motion.li>
                    ))}
                  </ul>
                </SideBlock>

                <SideBlock label="Publications" delay={0.65}>
                  <ul className="space-y-2">
                    {resumeData.publications.map((p, i) => (
                      <motion.li
                        key={p}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.7 + i * 0.05, ease }}
                        className="italic text-white/82"
                      >
                        “{p}”
                      </motion.li>
                    ))}
                  </ul>
                </SideBlock>
              </aside>

              <section className="relative px-6 py-8 md:col-span-8 md:px-10 md:py-12">
                <motion.p
                  {...fadeUp(0.05)}
                  className="text-[10px] font-semibold uppercase tracking-[0.32em] text-white/55"
                >
                  Profile
                </motion.p>

                <motion.h2
                  {...fadeUp(0.12)}
                  className="mt-3 font-display text-4xl font-bold uppercase leading-[0.9] tracking-[-0.025em] md:text-6xl"
                >
                  {resumeData.name.split(' ').map((word, i) => (
                    <motion.span
                      key={`${word}-${i}`}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.18 + i * 0.08, ease }}
                      className="mr-3 inline-block"
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.h2>

                <motion.p
                  {...fadeUp(0.4)}
                  className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-accent"
                >
                  {resumeData.headline}
                </motion.p>
                <motion.p {...fadeUp(0.46)} className="mt-1 text-xs uppercase tracking-[0.2em] text-white/55">
                  {resumeData.location}
                </motion.p>

                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.45, ease }}
                  className="mt-7 h-px origin-left bg-gradient-to-r from-accent via-white/30 to-transparent"
                />

                <motion.p {...fadeUp(0.55)} className="copy-refined mt-7 max-w-2xl text-sm text-white/82 md:text-base">
                  {resumeData.summary}
                </motion.p>

                <motion.div {...fadeUp(0.7)} className="mt-10">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">Experience</p>
                  <ol className="mt-5 space-y-7">
                    {resumeData.experience.map((exp, i) => (
                      <motion.li
                        key={exp.company}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.78 + i * 0.08, ease }}
                        className="relative rounded-2xl border border-white/12 bg-white/[0.04] p-5 backdrop-blur-sm md:p-6"
                      >
                        <div className="flex flex-col gap-1 border-b border-white/10 pb-3 md:flex-row md:items-baseline md:justify-between">
                          <h3 className="font-display text-xl font-bold uppercase tracking-[-0.01em] md:text-2xl">
                            {exp.company}
                          </h3>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/55">
                            {exp.span}
                          </span>
                        </div>

                        <ul className="mt-4 space-y-4">
                          {exp.roles.map((role) => (
                            <li key={role.title + role.period} className="relative pl-5">
                              <span className="absolute left-0 top-2 h-2 w-2 rounded-full bg-accent" />
                              <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between">
                                <p className="text-sm font-semibold uppercase tracking-[0.06em] text-white">
                                  {role.title}
                                </p>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">{role.period}</p>
                              </div>
                              {role.location && (
                                <p className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-white/45">
                                  {role.location}
                                </p>
                              )}
                              {role.note && <p className="copy-refined mt-2 text-sm text-white/75">{role.note}</p>}
                              {role.stack?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                  {role.stack.map((t) => (
                                    <span
                                      key={t}
                                      className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/80"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </motion.li>
                    ))}
                  </ol>
                </motion.div>

                <motion.div {...fadeUp(0.85)} className="mt-12">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">Education</p>
                  <ul className="mt-5 space-y-4">
                    {resumeData.education.map((ed, i) => (
                      <motion.li
                        key={ed.school}
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.92 + i * 0.07, ease }}
                        className="flex flex-col gap-1 border-l border-white/15 pl-4 md:flex-row md:items-baseline md:justify-between md:border-l-0 md:border-b md:pb-3 md:pl-0"
                      >
                        <div>
                          <p className="font-display text-base font-bold uppercase tracking-[-0.01em] md:text-lg">
                            {ed.school}
                          </p>
                          <p className="text-xs uppercase tracking-[0.2em] text-white/65">{ed.degree}</p>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-white/50">{ed.period}</p>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  {...fadeUp(1.0)}
                  className="mt-12 flex flex-col items-stretch gap-3 rounded-2xl border border-white/12 bg-white/[0.04] p-5 md:flex-row md:items-center md:justify-between md:p-6"
                >
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/60">
                      Like what you see?
                    </p>
                    <p className="mt-1 font-display text-lg font-bold uppercase tracking-[-0.01em] md:text-xl">
                      Let&apos;s build something together.
                    </p>
                  </div>
                  <a
                    href="https://wa.me/919573757096?text=Hi%20Nithin%2C%20saw%20your%20resume%20—%20want%20to%20discuss%20a%20project."
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white bg-white px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-coal transition-transform hover:-translate-y-0.5"
                  >
                    WhatsApp Me ↗
                  </a>
                </motion.div>
              </section>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Resume;
